/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { createDisposer, noop, type Unsubscribe } from '@amazon/vinyl-util'
import type { DynamicMessagingTriggerDeps } from '@/messaging/dm/dmTriggerBinding'
// Eager so the entry path is recorded at boot, not when the lazy DM chunk lands.
import '@/messaging/dm/isDeeplinkAppStart'

/**
 * Lazy boot seam for the Dynamic Messaging engine. The DM subtree (engine +
 * REST service + fallback-string bundle + ribbon/modal chrome) is a global
 * singleton nothing on first paint touches, so it's loaded off the boot chunk
 * here rather than statically imported by app.ts.
 *
 * Wires both boot-time DM hooks — the app-lifecycle triggers and the
 * locale-change cache reset — behind one dynamic import, and races the load:
 * if the app reloads before the module resolves, dispose tears down whatever
 * was installed by then via the accumulating disposer.
 */
export function initializeDynamicMessagingLazy(deps: DynamicMessagingTriggerDeps): Unsubscribe {
    const disposer = createDisposer()
    import('@/messaging/dm/dmBootstrapImpl')
        .then((m) => {
            if (disposer.disposed) return
            disposer.add(m.initializeDynamicMessaging(deps))
        })
        .catch(noop)
    return disposer.dispose
}
